import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { Fetcher } from '../../../Helpers/fetcher';
import { Notify } from '../../../Helpers/notification';
import { Card, Form, Button, InputGroup, Alert } from 'react-bootstrap';
import { FaShieldAlt, FaSearch, FaSave, FaInfoCircle } from 'react-icons/fa';
import "./index.css"
import { buildAclPayload, buildModuleListingPayload, hasAnyPermissionSelected, sortModules, MANAGE_USERS_SETTING_OPTIONS, NUMBER_ACTION_OPTIONS } from '../helpers/aclHelper';
import AclTable from '../components/AclTable';
const CreateAcl = ({ app }) => {
    const apiPath = app.apiPath;
    const userId = app.userId
    const history = useHistory();

    const [aclName, setAclName] = useState('');
    const [moduleList, setModuleList] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [search, setSearch] = useState('');
    const [expandedModules, setExpandedModules] = useState({});
    const [loading, setLoading] = useState(false);
    const [moduleListingData, setModuleListingData] = useState({});
    const [multiSelectOptions, setMultiSelectOptions] = useState({});

    const fetchModuleData = async () => {
    try {
        const dialerUrl = `${apiPath}/api/obd/dialers/unallocated`;
        const moduleUrl = `${apiPath}/api/obd/module`;
        const locationUrl = `${apiPath}/api/obd/locations/${userId}`;

        const transform = (arr, label, value) =>
            (Array.isArray(arr) ? arr : []).map(item => ({
                name: item?.[label] ?? "",
                id: item?.[value] ?? "",
                dialerName: item?.dialerName ?? ""
            }));

        const [dialerRes, moduleRes, locationRes] = await Promise.all([
            Fetcher.get(dialerUrl),
            Fetcher.get(moduleUrl),
            Fetcher.get(locationUrl).catch(() => [])
        ]);

        setMultiSelectOptions({
            "Manage Dialers": transform(dialerRes, "dialerName", "dialerId"),
            "Manage Users": [...transform(moduleRes, "moduleName", "moduleId"), ...MANAGE_USERS_SETTING_OPTIONS],
            "Manage Locations": transform(locationRes, "locationName", "locationId"),
            "Number Action": NUMBER_ACTION_OPTIONS
        });

    } catch (err) {
            console.log("fetchModuleData error", err);
            Notify.notify("Error loading dropdown data");
        }
    };

    const getModuleList = async () => {
        setLoading(true);
        try {
            const res = await Fetcher.get(`${apiPath}/api/obd/acl/module/list`);

            const data = (Array.isArray(res) ? res : []).map(m => {
                let children = [];

                try {
                    children = Array.isArray(m.childModule)
                        ? m.childModule
                        : JSON.parse(m.childModule || "[]");
                } catch {
                    children = [];
                }

                return {
                    ...m,
                    children: children.filter(c => c.moduleName !== "Create Location")
                };
            });

            setModuleList(data);

            const init = {};

            data.forEach(m => {
                const hasChild = m.children?.length > 0;

                init[m.mname] = hasChild
                    ? { read: false, view_permission: false }
                    : {
                        read: false,
                        add: false,
                        edit: false,
                        delete: false,
                        view_permission: false
                    };

                // Children
                if (hasChild) {
                    m.children.forEach(c => {
                        init[c.moduleName] = {
                            read: false,
                            add: false,
                            edit: false,
                            delete: false,
                            view_permission: false
                        };
                    });
                }
            });

            setPermissions(init);

        } catch {
            Notify.notify("Error loading modules");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModuleData();
        getModuleList();
    }, []);

    const togglePermission = (id, key) => {
        setPermissions(prev => {
            const updated = {
                ...prev,
                [id]: {
                    ...(prev[id] || {}),
                    [key]: !prev?.[id]?.[key]
                }
            };

            if (
                key === "view_permission" &&
                updated[id]?.view_permission === false
            ) {
                setModuleListingData(prev => {
                    const copy = { ...prev };
                    delete copy[id];
                    return copy;
                });
            }

            return updated;
        });
    };

    const toggleAll = (id, keys, checked) => {
        setPermissions(prev => {
            const updated = { ...prev };
            if (!updated[id]) updated[id] = {};

            keys.forEach(k => {
                updated[id][k] = checked;
            });

            return updated;
        });
    };

    const isAllChecked = (id, keys) => {
        const p = permissions[id] || {};
        return keys.every(k => p[k] === true);
    };

    const toggleExpand = (id) => {
        setExpandedModules(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const filteredModules = moduleList.sort(sortModules).filter(m =>
        m.mname.toLowerCase().includes(search.toLowerCase())
    );

    const handleSubmit = () => {
        if (!aclName.trim()) {
            Notify.notify("ACL Name is required");
            return;
        }

        if (!hasAnyPermissionSelected(moduleList, permissions)) {
            Notify.notify("Please select at least one permission before updating");
            return;
        }

        const cleanedModuleListingData = buildModuleListingPayload(moduleListingData)
        const payload = {
            ...buildAclPayload({
                moduleList,
                permissions,
                aclName,
                userId,
            }),
            moduleListingData: JSON.stringify(cleanedModuleListingData)
        };

        Fetcher.post(`${apiPath}/api/obd/add/acl/settings`, {
            method: "POST",
            headers: { "Content-type": "application/json" },
            body: JSON.stringify(payload)
        })
            .then(res => {
                if (res.message === 'Parameters Incorrect') {
                    Notify.notify('Parameters Incorrect');
                } else {
                    Notify.notify(res.message);
                    history.push("/acl-settings");
                }
            })
            .catch(err => {
                console.log('Error in Adding Acl', err);
                Notify.notify('Error in Adding Acl');
            });
    };
    return (
        <Card className="acl-card border-0 shadow-sm">
            <Card.Header className="acl-card-header">
                <div className="acl-header-icon">
                    <FaShieldAlt size={18} />
                </div>
                <div>
                    <h5 className="acl-header-title">Create ACL</h5>
                    <p className="acl-header-subtitle">Define a new access role and its module-level permissions</p>
                </div>
            </Card.Header>
            <Card.Body className="acl-card-body">

                <div className="acl-section">
                    <div className="acl-section-title">Basic Details</div>
                    <Form.Group className="mb-0">
                        <Form.Label>ACL Name</Form.Label>
                        <Form.Control
                            value={aclName}
                            onChange={(e) => setAclName(e.target.value)}
                            placeholder="Enter ACL name"
                        />
                    </Form.Group>
                </div>

                <Alert variant='warning' className="acl-alert">
                    <FaInfoCircle className="mt-1" />
                    <span>Note: Only modules with View Permission enabled will be visible in the user sidebar.</span>
                </Alert>

                <div className="acl-section">
                    <div className="acl-section-title">Module Permissions</div>

                    <InputGroup className="acl-search mb-3">
                        <InputGroup.Text className="acl-search-icon">
                            <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Search module name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </InputGroup>

                    <div className="acl-table-wrapper">
                        <AclTable
                            loading={loading}
                            filteredModules={filteredModules}
                            permissions={permissions}
                            expandedModules={expandedModules}
                            moduleListingData={moduleListingData}
                            multiSelectOptions={multiSelectOptions}
                            togglePermission={togglePermission}
                            toggleAll={toggleAll}
                            isAllChecked={isAllChecked}
                            toggleExpand={toggleExpand}
                            setModuleListingData={setModuleListingData}
                        />
                    </div>
                </div>

            </Card.Body>

            <Card.Footer className="acl-card-footer">
                <Button onClick={handleSubmit} className="acl-save-btn">
                    <FaSave />
                    Save ACL
                </Button>
            </Card.Footer>
        </Card>
    );
};

export default CreateAcl;